/**
 * useDeploymentHistory.ts
 * Feature: 040-deployment-history — Deployment History Tracking
 * Author: Peter Dobler
 * Date: 2026-03-28
 * Constitution Version: 1.12.0
 *
 * Purpose: React Query hook for fetching deployment history records.
 */

import { useQuery } from '@tanstack/react-query';
import { getHistory } from '../services/deploymentHistoryService';
import type {
  DeploymentHistoryFilters,
  DeploymentHistoryResponse,
} from '@shared/types/deploymentHistory';

// =============================================================================
// Query key factory
// =============================================================================

export const deploymentHistoryKeys = {
  all: ['deployment-history'] as const,
  list: (filters: DeploymentHistoryFilters) =>
    [...deploymentHistoryKeys.all, 'list', filters] as const,
};

// =============================================================================
// useDeploymentHistory
// =============================================================================

/**
 * Fetch a paginated, server-side-filtered list of deployment history records.
 *
 * @param filters — environment, component, date range, page, pageSize
 * @returns React Query result containing DeploymentHistoryResponse
 */
export function useDeploymentHistory(filters: DeploymentHistoryFilters = {}) {
  return useQuery<DeploymentHistoryResponse, Error>({
    queryKey: deploymentHistoryKeys.list(filters),
    queryFn: () => getHistory(filters),
    staleTime: 30_000, // 30 seconds
  });
}
